"use client"

import type { MetaClientParameterBuilder } from "meta-capi-param-builder-clientjs"

export type MetaClientParams = {
  fbc?: string
  fbp?: string
}

export type MetaClientBuilderStatus = "processed" | "fallback"

type MetaClientModuleShape = Record<string, unknown>

const boundedOpaqueValue = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined
  const normalized = value.trim()
  return normalized && normalized.length <= 512 ? normalized : undefined
}

const readCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined
  let cookieHeader = ""
  try {
    cookieHeader = document.cookie || ""
  } catch {
    return undefined
  }
  for (const entry of cookieHeader ? cookieHeader.split(";") : []) {
    const [key, ...rest] = entry.split("=")
    if (key?.trim() !== name) continue
    const raw = rest.join("=").trim()
    if (!raw) return undefined
    try {
      return decodeURIComponent(raw)
    } catch {
      return raw
    }
  }
  return undefined
}

const isBuilder = (value: unknown): value is MetaClientParameterBuilder => {
  if (!value || typeof value !== "object") return false
  const candidate = value as Partial<MetaClientParameterBuilder>
  return (
    typeof candidate.processAndCollectAllParams === "function" &&
    typeof candidate.getFbc === "function" &&
    typeof candidate.getFbp === "function"
  )
}

export const normalizeMetaClientBuilderModule = (
  moduleValue: unknown
): MetaClientParameterBuilder => {
  const queue: unknown[] = [moduleValue]
  const visited = new Set<unknown>()
  while (queue.length > 0) {
    const candidate = queue.shift()
    if (visited.has(candidate)) continue
    visited.add(candidate)
    if (isBuilder(candidate)) return candidate
    if (candidate && typeof candidate === "object") {
      const record = candidate as MetaClientModuleShape
      queue.push(record.default, record.clientParamBuilder)
    }
  }
  throw new Error("Meta client Parameter Builder module shape is unsupported")
}

const snapshotFromBuilder = (
  builder: MetaClientParameterBuilder | null,
  returned?: unknown
): MetaClientParams => {
  const result =
    returned && typeof returned === "object"
      ? (returned as Record<string, unknown>)
      : undefined
  const fbc = boundedOpaqueValue(builder?.getFbc()) ||
    boundedOpaqueValue(result?._fbc) ||
    boundedOpaqueValue(readCookie("_fbc"))
  const fbp = boundedOpaqueValue(builder?.getFbp()) ||
    boundedOpaqueValue(result?._fbp) ||
    boundedOpaqueValue(readCookie("_fbp"))
  return {
    ...(fbc ? { fbc } : {}),
    ...(fbp ? { fbp } : {}),
  }
}

export const createMetaClientParamAdapter = ({
  loadModule,
}: {
  loadModule: () => Promise<unknown>
}) => {
  let builderPromise: Promise<MetaClientParameterBuilder> | null = null
  let loadedBuilder: MetaClientParameterBuilder | null = null
  let processingTail: Promise<void> = Promise.resolve()
  const inFlightByUrl = new Map<string, Promise<MetaClientParams>>()
  const outcomeByUrl = new Map<string, MetaClientBuilderStatus>()
  let lastProcessedUrl: string | null = null
  let failures = 0

  const load = () => {
    if (!builderPromise) {
      builderPromise = loadModule()
        .then(normalizeMetaClientBuilderModule)
        .then((builder) => {
          loadedBuilder = builder
          return builder
        })
        .catch((error) => {
          builderPromise = null
          throw error
        })
    }
    return builderPromise
  }

  const read = (): MetaClientParams => snapshotFromBuilder(loadedBuilder)

  const process = (url: string): Promise<MetaClientParams> => {
    const normalizedUrl = typeof url === "string" ? url.trim() : ""
    if (!normalizedUrl) return Promise.resolve(read())
    const existing = inFlightByUrl.get(normalizedUrl)
    if (existing) return existing
    if (lastProcessedUrl === normalizedUrl) return Promise.resolve(read())

    const execute = processingTail
      .catch(() => undefined)
      .then(async () => {
        try {
          const builder = await load()
          // No getIpFn is supplied: the library must not make an external IP call.
          const returned = await builder.processAndCollectAllParams(normalizedUrl)
          lastProcessedUrl = normalizedUrl
          outcomeByUrl.set(normalizedUrl, "processed")
          return snapshotFromBuilder(builder, returned)
        } catch {
          failures += 1
          outcomeByUrl.set(normalizedUrl, "fallback")
          return read()
        }
      })
    processingTail = execute.then(() => undefined)
    const promise = execute.finally(() => {
      if (inFlightByUrl.get(normalizedUrl) === promise) {
        inFlightByUrl.delete(normalizedUrl)
      }
    })
    inFlightByUrl.set(normalizedUrl, promise)
    return promise
  }

  return {
    process,
    processWithStatus: async (url: string) => {
      const normalizedUrl = typeof url === "string" ? url.trim() : ""
      const params = await process(normalizedUrl)
      return {
        params,
        status: normalizedUrl
          ? outcomeByUrl.get(normalizedUrl) || "fallback"
          : "fallback",
      } as const
    },
    read,
    diagnostics: () => ({ failures, lastProcessedUrl }),
  }
}

const browserAdapter = createMetaClientParamAdapter({
  loadModule: () => import("meta-capi-param-builder-clientjs"),
})

export const processMetaClientParams = (
  url = typeof window !== "undefined" ? window.location.href : ""
): Promise<MetaClientParams> => browserAdapter.process(url)

export const processMetaClientParamsWithStatus = (
  url = typeof window !== "undefined" ? window.location.href : ""
) => browserAdapter.processWithStatus(url)

export const readMetaClientParams = (): MetaClientParams => browserAdapter.read()

export const getMetaClientParamBuilderDiagnostics = () =>
  browserAdapter.diagnostics()
